/**
 * --------------------------------------------------------------------------
 * CoreUI PRO scheduler.ts
 * License (https://coreui.io/pro/license/)
 * --------------------------------------------------------------------------
 */

import {
  computeDayListModel,
  computeMonthModel,
  computeTimelineModel,
  computeViewModel,
  createInteractionMachine,
  createSchedulerStore,
  exactDuration,
  interpolate,
  getAgendaDays,
  getMonthDays,
  overlaps,
  resolveNavigation,
  DEFAULT_LABELS,
  toISO,
  toZoned,
  MINUTES_IN_DAY,
  type DateInput,
  type DayOccurrences,
  type EventOccurrence,
  type EventPatch,
  type InteractionCommit,
  type InteractionMachine,
  type MonthModel,
  type NormalizedPointer,
  type OccurrenceGeometry,
  type PressTarget,
  type RecurringEditScope,
  type SchedulerEvent,
  type SchedulerLabels,
  type SchedulerResource,
  type SchedulerStore,
  type SchedulerView,
  type SchedulerViewModel,
  type SelectionRange,
  type TimelineModel
} from '@coreui/scheduler-core'
import { Temporal } from 'temporal-polyfill'
import BaseComponent from './base-component.js'
import EventHandler from './dom/event-handler.js'
import { createRenderer, type BusinessHours, type Renderer } from './render.js'
import type { ComponentConfig } from './util/config.js'

/**
 * Constants
 */

const NAME = 'scheduler'
const DATA_KEY = 'coreui.scheduler'
const EVENT_KEY = `.${DATA_KEY}`

const EVENT_DATE_CHANGE = `dateChange${EVENT_KEY}`
const EVENT_EVENT_CHANGE = `eventChange${EVENT_KEY}`
const EVENT_SELECTION_CHANGE = `selectionChange${EVENT_KEY}`
const EVENT_VIEW_CHANGE = `viewChange${EVENT_KEY}`

const SELECTOR_EVENT = '[data-part="event"]'
const SELECTOR_COLUMN = '[data-part="column"]'
const SELECTOR_DELETE_BUTTON = '[data-part="delete-button"]'
const SELECTOR_MONTH_DAY = '[data-part="month-day"]'
const SELECTOR_DIALOG = '[data-part="event-dialog"]'
const SELECTOR_RESIZE_START = '[data-part="resize-handle-start"]'
const SELECTOR_RESIZE_END = '[data-part="resize-handle-end"]'
const SELECTOR_TIME_SLOT = '[data-part="time-slot"]'

const NAVIGATION_KEYS: Record<string, 'up' | 'down' | 'left' | 'right' | 'home' | 'end'> = {
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
  ArrowUp: 'up',
  End: 'end',
  Home: 'home'
}

export interface SchedulerDataSource {
  getEvents: (start: string, end: string) => Promise<SchedulerEvent[]> | SchedulerEvent[]
  persistEvents?: (change: {
    action: string
    event: SchedulerEvent
    occurrence: { key: string; start: string; end: string } | null
    scope: RecurringEditScope | null
  }) => Promise<{ success?: boolean } | void> | { success?: boolean } | void
}

export interface SchedulerConfig extends ComponentConfig {
  agendaDays: number
  businessHours: BusinessHours | null
  dataSource: SchedulerDataSource | null
  date: DateInput | null
  dayEndHour: number
  dayStartHour: number
  events: SchedulerEvent[]
  eventOverlap: boolean
  expandToFit: boolean
  firstDayOfWeek: number
  hourHeight: number
  labels: Partial<SchedulerLabels>
  locale: string | null
  monthMaxEventsPerDay: number
  recurringEditScope: RecurringEditScope
  resourceColumnWidth: number
  resources: SchedulerResource[]
  selectable: boolean
  snap: number
  timeZone: string | null
  timelineDays: number
  timelineHourWidth: number
  view: SchedulerView
}

const Default: SchedulerConfig = {
  agendaDays: 14,
  businessHours: null,
  dataSource: null,
  date: null,
  dayEndHour: 24,
  dayStartHour: 0,
  events: [],
  eventOverlap: true,
  expandToFit: false,
  firstDayOfWeek: 1,
  hourHeight: 48,
  labels: {},
  locale: null,
  monthMaxEventsPerDay: 4,
  recurringEditScope: 'occurrence',
  resourceColumnWidth: 160,
  resources: [],
  selectable: true,
  snap: 15,
  timeZone: null,
  timelineDays: 7,
  timelineHourWidth: 48,
  view: 'week'
}

const DefaultType = {
  agendaDays: 'number',
  businessHours: '(object|null)',
  dataSource: '(object|null)',
  date: '(string|date|null)',
  dayEndHour: 'number',
  dayStartHour: 'number',
  events: 'array',
  eventOverlap: 'boolean',
  expandToFit: 'boolean',
  firstDayOfWeek: 'number',
  hourHeight: 'number',
  labels: 'object',
  locale: '(string|null)',
  monthMaxEventsPerDay: 'number',
  recurringEditScope: 'string',
  resourceColumnWidth: 'number',
  resources: 'array',
  selectable: 'boolean',
  snap: 'number',
  timeZone: '(string|null)',
  timelineDays: 'number',
  timelineHourWidth: 'number',
  view: 'string'
}

const NOW_REFRESH_MS = 60_000
const TOUCH_DRAG_DELAY_MS = 300
const TOUCH_SCROLL_TOLERANCE_PX = 8

let uid = 0

/**
 * Class definition
 */

class Scheduler extends BaseComponent {
  declare _config: SchedulerConfig
private _store!: SchedulerStore
  private _machine!: InteractionMachine
  private _renderer!: Renderer
  private _labels!: SchedulerLabels
  private _viewModel: SchedulerViewModel | null = null
  private _dayList: DayOccurrences[] | null = null
  private _month: MonthModel | null = null
  private _timeline: TimelineModel | null = null
  private _occurrences: EventOccurrence[] = []
  private _unsubscribe!: () => void
  private _nowTimer: ReturnType<typeof setInterval> | null = null
  private _machineMode: 'grid' | 'month' | 'timeline' = 'grid'
  private _pointerSession: { pointerId: number } | null = null
  private _keyboardGrab: string | null = null
  private _slotFocus: { column: number; slot: number } = { column: 0, slot: 0 }
  private _slotSelection: { column: number; minutes: number } | null = null
  private _dialog: string | null = null
  private _dialogFocusPending = false
  private _fetchedRange: string | null = null
  private _fetchToken = 0
  private _touchPending: {
    pointerId: number
    x: number
    y: number
    target: PressTarget
    timer: ReturnType<typeof setTimeout>
  } | null = null
  private _touchDragging = false
// Non-passive so an activated long-press drag can suppress scrolling for
  // the rest of the gesture; before activation a real pan cancels the press.
  private _onTouchMove = (event: TouchEvent) => {
    if (this._touchDragging) {
      event.preventDefault()
      return
    }

    const pending = this._touchPending
    const touch = event.touches[0]

    if (!pending || !touch) {
      return
    }

    if (Math.hypot(touch.clientX - pending.x, touch.clientY - pending.y) > TOUCH_SCROLL_TOLERANCE_PX) {
      this._clearTouchPress()
    }
  }
private _onWindowPointerMove = (event: PointerEvent) => this._handlePointerMove(event)
  private _onWindowPointerUp = (event: PointerEvent) => this._handlePointerUp(event)
  private _onWindowPointerCancel = () => this._cancelInteraction()
// During a pointer drag focus sits outside the root, so the root keydown
  // listener never sees Escape — the window listener lives only for the
  // session.
  private _onWindowKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      this._cancelInteraction()
    }
  }

  constructor(element: string | Element, config?: Partial<SchedulerConfig>) {
    super(element, config)

    this._labels = { ...DEFAULT_LABELS, ...this._config.labels }
    this._slotFocus = { column: 0, slot: Math.max(0, 8 - this._config.dayStartHour) }

    this._store = createSchedulerStore({
      date: this._config.date ?? undefined,
      events: this._config.events,
      resources: this._config.resources,
      timeZone: this._config.timeZone ?? undefined,
      view: this._config.view
    })

    this._machine = this._createMachine()

    this._renderer = createRenderer(this._element, {
      onNavigate: direction => this.navigate(direction),
      onViewSelect: view => this.setView(view),
      onWindowChange: () => this._applySlotFocus(),
      onMoreLink: dateISO => {
        this._store.setDate(dateISO)
        this.setView('day')
      }
    })

    this._unsubscribe = this._store.store.subscribe(() => this._render()).unsubscribe
    this._addEventListeners()
    this._startNowTimer()
    this._render()
  }

  // Getters
  static override get Default(): SchedulerConfig {
    return Default
  }

  static override get DefaultType(): ComponentConfig {
    return DefaultType
  }

  static override get NAME(): string {
    return NAME
  }

  // Public
  getEvents(): SchedulerEvent[] {
    return [...this._store.store.state.events]
  }

  getSelection(): string[] {
    return [...this._store.store.state.selection]
  }

  /** The currently visible window as ISO strings (end exclusive). */
  getVisibleRange(): { start: string; end: string } | null {
    const window = this._visibleWindow()
    return window ? { start: toISO(window.start), end: toISO(window.end) } : null
  }

  navigate(direction: 'prev' | 'next' | 'today'): void {
    const { view } = this._store.store.state
    const step = view === 'agenda' ? this._config.agendaDays : (view === 'timeline' ? this._config.timelineDays : undefined)
    this._store.navigate(direction, step)
    EventHandler.trigger(this._element, EVENT_DATE_CHANGE, { date: this._store.store.state.date })
  }

  setDate(date: DateInput): void {
    this._store.setDate(date)
    EventHandler.trigger(this._element, EVENT_DATE_CHANGE, { date: this._store.store.state.date })
  }

  setEvents(events: SchedulerEvent[]): void {
    this._store.setEvents(events)
  }

  setView(view: SchedulerView): void {
    if (view === this._store.store.state.view) {
      return
    }

    this._store.setView(view)
    EventHandler.trigger(this._element, EVENT_VIEW_CHANGE, { view })
  }

  update(config: Partial<SchedulerConfig>): void {
    this._config = this._getConfig({ ...this._config, ...config }) as SchedulerConfig
    this._labels = { ...DEFAULT_LABELS, ...this._config.labels }
    this._machine = this._createMachine()

    if (config.events) {
      this._store.setEvents(config.events)
    }

    if (config.resources) {
      this._store.setResources(config.resources)
    }

    if (config.view) {
      this._store.setView(config.view)
    }

    if (config.date) {
      this._store.setDate(config.date)
    }

    if ('dataSource' in config) {
      this._fetchedRange = null
    }

    this._render()
  }

  override dispose(): void {
    this._machine.cancel()
    this._clearTouchPress()
    this._endTouchDrag()
    this._teardownWindowListeners()

    if (this._nowTimer) {
      clearInterval(this._nowTimer)
    }

    this._unsubscribe()
    this._renderer.dispose()
    EventHandler.off(this._element, EVENT_KEY)
    super.dispose()
  }

  // Private
  private _machineModeFor(view: SchedulerView): 'grid' | 'month' | 'timeline' {
    if (view === 'month') {
      return 'month'
    }

    return view === 'timeline' ? 'timeline' : 'grid'
  }

  private _createMachine(): InteractionMachine {
    const mode = this._machineModeFor(this._store?.store.state.view ?? 'week')
    this._machineMode = mode

    const bounds =
      mode === 'month' ?
        { minMinutes: 0, dayMinutes: 0 } :
        (mode === 'timeline' ?
          { minMinutes: 0, dayMinutes: this._config.timelineDays * MINUTES_IN_DAY } :
          { minMinutes: this._config.dayStartHour * 60, dayMinutes: this._config.dayEndHour * 60 })

    return createInteractionMachine({
      snap: this._config.snap,
      ...bounds,
      onChange: state => this._renderer.preview(state),
      onCommit: commit => this._handleCommit(commit),
      onClick: click => this._handleClick(click)
    })
  }

  private _now(): Temporal.ZonedDateTime {
    return Temporal.Now.zonedDateTimeISO(this._store.store.state.timeZone)
  }

  private _startNowTimer(): void {
    this._nowTimer = setInterval(() => {
      this._renderer.updateNow(this._now())
    }, NOW_REFRESH_MS)
  }

  private _isTimeGrid(): boolean {
    const { view } = this._store.store.state
    return view === 'day' || view === 'week' || view === 'resource'
  }

  private _columnCount(): number {
    const { view } = this._store.store.state

    if (view === 'month') {
      return this._month?.days.length ?? 0
    }

    if (view === 'timeline') {
      return this._timeline?.rows.length ?? 0
    }

    return this._viewModel?.columns.length ?? 0
  }

  private _render(): void {
    const { state } = this._store.store
    const focusedKey =
      document.activeElement instanceof HTMLElement && this._element.contains(document.activeElement) ?
        (document.activeElement.closest(SELECTOR_EVENT) as HTMLElement | null)?.dataset.coreuiKey :
        undefined

    if (this._machineViewMismatch()) {
      this._machine = this._createMachine()
    }

    if (this._isTimeGrid()) {
      this._viewModel = computeViewModel(state, {
        dayEndMinutes: this._config.dayEndHour * 60,
        dayStartMinutes: this._config.dayStartHour * 60,
        expandToFit: this._config.expandToFit,
        firstDayOfWeek: this._config.firstDayOfWeek,
        minEventMinutes: this._config.snap
      })
      this._dayList = null
      this._timeline = null
      this._month = null
      this._occurrences = this._viewModel.occurrences
    } else if (state.view === 'timeline') {
      this._viewModel = null
      this._dayList = null
      this._month = null
      this._timeline = computeTimelineModel(state, getAgendaDays(state, this._config.timelineDays), {
        minEventMinutes: this._config.snap
      })
      this._occurrences = this._timeline.occurrences
    } else if (state.view === 'month') {
      this._viewModel = null
      this._timeline = null
      this._dayList = null
      this._month = computeMonthModel(state, getMonthDays(state, this._config.firstDayOfWeek))
      this._occurrences = this._month.occurrences
    } else {
      this._viewModel = null
      this._timeline = null
      this._month = null
      this._dayList = computeDayListModel(state, getAgendaDays(state, this._config.agendaDays))
      const seen = new Map<string, EventOccurrence>()

      for (const entry of this._dayList) {
        for (const occurrence of entry.occurrences) {
          seen.set(occurrence.key, occurrence)
        }
      }

      this._occurrences = [...seen.values()]
    }

    this._machine.setColumnCount(this._columnCount())

    const dialogOccurrence = this._dialog ? this._occurrenceByKey(this._dialog) ?? null : null

    if (this._dialog && !dialogOccurrence) {
      this._dialog = null
    }

    this._renderer.render({
      businessHours: this._config.businessHours,
      dayEndHour: this._config.dayEndHour,
      dayStartHour: this._config.dayStartHour,
      dayList: this._dayList,
      dialogOccurrence,
      hourHeight: this._config.hourHeight,
      labels: this._labels,
      locale: this._config.locale ?? undefined,
      monthMaxEventsPerDay: this._config.monthMaxEventsPerDay,
      monthModel: this._month,
      now: this._now(),
      recurringEditScope: this._config.recurringEditScope,
      resourceColumnWidth: this._config.resourceColumnWidth,
      resources: state.resources,
      selection: state.selection,
      showResourceButton: state.resources.length > 0,
      timeZone: state.timeZone,
      timelineHourWidth: this._config.timelineHourWidth,
      timelineModel: this._timeline,
      view: state.view,
      viewModel: this._viewModel
    })

    if (this._isTimeGrid()) {
      this._slotFocus = {
        column: Math.min(this._slotFocus.column, Math.max(this._columnCount() - 1, 0)),
        slot: Math.min(this._slotFocus.slot, Math.max(this._config.dayEndHour - this._config.dayStartHour - 1, 0))
      }
      this._applySlotFocus()
    }

    if (this._dialogFocusPending && dialogOccurrence) {
      this._dialogFocusPending = false
      this._renderer.focusDialog()
    } else if (focusedKey && !dialogOccurrence) {
      this._renderer.focusEvent(focusedKey)
    }

    this._maybeFetch()
  }

  private _visibleWindow(): { start: Temporal.ZonedDateTime; end: Temporal.ZonedDateTime } | null {
    if (this._viewModel) {
      return this._viewModel.window
    }

    if (this._timeline) {
      return this._timeline.window
    }

    const days = this._month?.days ?? this._dayList?.map(entry => entry.date)

    if (days && days.length > 0) {
      const { timeZone } = this._store.store.state
      return {
        start: days[0].toZonedDateTime({ timeZone }),
        end: days[days.length - 1].add({ days: 1 }).toZonedDateTime({ timeZone })
      }
    }

    return null
  }

  private _maybeFetch(): void {
    const { dataSource } = this._config
    const window = this._visibleWindow()

    if (!dataSource || !window) {
      return
    }

    const key = `${window.start.epochMilliseconds}|${window.end.epochMilliseconds}`

    if (key === this._fetchedRange) {
      return
    }

    this._fetchedRange = key
    this._fetchToken += 1
    const token = this._fetchToken
    this._renderer.setLoading(true)

    Promise.resolve(dataSource.getEvents(toISO(window.start), toISO(window.end)))
      .then(events => {
        if (token === this._fetchToken) {
          this._store.setEvents(events)
        }
      })
      .catch(() => {
        if (token === this._fetchToken) {
          this._renderer.announce(this._labels.loadFailed)
        }
      })
      .finally(() => {
        if (token === this._fetchToken) {
          this._renderer.setLoading(false)
        }
      })
  }

  private _machineViewMismatch(): boolean {
    return this._machineModeFor(this._store.store.state.view) !== this._machineMode
  }

  private _addEventListeners(): void {
    EventHandler.on(this._element, `pointerdown${EVENT_KEY}`, SELECTOR_COLUMN, (event: Event) =>
      this._handlePointerDown(event as PointerEvent)
    )

    EventHandler.on(this._element, `pointerdown${EVENT_KEY}`, '.scheduler-month-week', (event: Event) =>
      this._handlePointerDown(event as PointerEvent)
    )

    EventHandler.on(this._element, `pointerdown${EVENT_KEY}`, '[data-part="timeline-row"]', (event: Event) =>
      this._handlePointerDown(event as PointerEvent)
    )

    EventHandler.on(this._element, `pointerdown${EVENT_KEY}`, '.scheduler-agenda', (event: Event) =>
      this._handleAgendaPointerDown(event as PointerEvent)
    )

    EventHandler.on(this._element, `pointerdown${EVENT_KEY}`, '[data-part="all-day-lane"]', (event: Event) => {
      const key = ((event.target as HTMLElement).closest(SELECTOR_EVENT) as HTMLElement | null)?.dataset.coreuiKey

      if (key && this._config.selectable) {
        this._handleClick({ occurrenceKey: key })
      }
    })

    EventHandler.on(this._element, `keydown${EVENT_KEY}`, SELECTOR_EVENT, (event: Event) =>
      this._handleEventKeydown(event as KeyboardEvent)
    )

    EventHandler.on(this._element, `keydown${EVENT_KEY}`, SELECTOR_TIME_SLOT, (event: Event) =>
      this._handleSlotKeydown(event as KeyboardEvent)
    )

    EventHandler.on(this._element, `keydown${EVENT_KEY}`, (event: Event) => {
      if ((event as KeyboardEvent).key === 'Escape') {
        this._cancelInteraction()
      }
    })

    EventHandler.on(this._element, `submit${EVENT_KEY}`, (event: Event) => {
      if ((event.target as HTMLElement).closest(SELECTOR_DIALOG)) {
        event.preventDefault()
        this._saveDialog()
      }
    })

    EventHandler.on(this._element, `click${EVENT_KEY}`, '[data-part="dialog-cancel"]', () => this._closeDialog())

    EventHandler.on(this._element, `pointerdown${EVENT_KEY}`, SELECTOR_DIALOG, (event: Event) => {
      if ((event.target as HTMLElement).dataset.part === 'event-dialog') {
        this._closeDialog()
      }
    })

    EventHandler.on(this._element, `click${EVENT_KEY}`, SELECTOR_DELETE_BUTTON, (event: Event) => {
      const key = ((event.target as HTMLElement).closest(SELECTOR_DIALOG) as HTMLElement | null)?.dataset.coreuiKey

      if (key) {
        this._deleteOccurrence(key)
      }
    })
  }

  private _setupWindowListeners(): void {
    window.addEventListener('pointermove', this._onWindowPointerMove)
    window.addEventListener('pointerup', this._onWindowPointerUp)
    window.addEventListener('pointercancel', this._onWindowPointerCancel)
    window.addEventListener('keydown', this._onWindowKeyDown)
  }

  private _teardownWindowListeners(): void {
    window.removeEventListener('pointermove', this._onWindowPointerMove)
    window.removeEventListener('pointerup', this._onWindowPointerUp)
    window.removeEventListener('pointercancel', this._onWindowPointerCancel)
    window.removeEventListener('keydown', this._onWindowKeyDown)
  }

  private _normalizePointer(event: PointerEvent): NormalizedPointer {
    const gridRect = this._renderer.refs.grid.getBoundingClientRect()

    if (this._store.store.state.view === 'month') {
      // Month rows grow with their chip count, so uniform rect math misses;
      // hit-test the actual cell instead.
      const cell = document.elementFromPoint(event.clientX, event.clientY)?.closest?.(SELECTOR_MONTH_DAY) as
        | HTMLElement
        | null

      if (cell?.dataset.coreuiDayIndex !== undefined) {
        return {
          point: { column: Number(cell.dataset.coreuiDayIndex), minutes: 0 },
          pixel: { x: event.clientX, y: event.clientY }
        }
      }

      const columnWidth = Math.max(gridRect.width / 7, 1)
      const col = Math.min(Math.max(Math.floor((event.clientX - gridRect.left) / columnWidth), 0), 6)
      const weekRows = [...this._element.querySelectorAll<HTMLElement>('.scheduler-month-week')]
      let row = 0
      let best = Number.POSITIVE_INFINITY

      for (const [index, weekRow] of weekRows.entries()) {
        const rect = weekRow.getBoundingClientRect()

        if (event.clientY >= rect.top && event.clientY < rect.bottom) {
          row = index
          break
        }

        const distance = event.clientY < rect.top ? rect.top - event.clientY : event.clientY - rect.bottom

        if (distance < best) {
          best = distance
          row = index
        }
      }

      return {
        point: { column: (row * 7) + col, minutes: 0 },
        pixel: { x: event.clientX, y: event.clientY }
      }
    }

    if (this._store.store.state.view === 'timeline') {
      const rowFromPoint = document.elementFromPoint(event.clientX, event.clientY)?.closest?.('[data-part="timeline-row"]') as
        | HTMLElement
        | null
      let row = rowFromPoint?.dataset.coreuiRow === undefined ? -1 : Number(rowFromPoint.dataset.coreuiRow)

      if (row === -1) {
        const rows = [...this._element.querySelectorAll<HTMLElement>('[data-part="timeline-row"]')]
        let best = Number.POSITIVE_INFINITY

        for (const candidate of rows) {
          const rect = candidate.getBoundingClientRect()
          const distance = event.clientY < rect.top ? rect.top - event.clientY : Math.max(0, event.clientY - rect.bottom)

          if (distance < best) {
            best = distance
            row = Number(candidate.dataset.coreuiRow)
          }
        }
      }

      const canvas = this._element.querySelector<HTMLElement>('[data-part="timeline-canvas"]')
      const canvasRect = canvas?.getBoundingClientRect()
      const ppmH = this._config.timelineHourWidth / 60
      const total = (this._timeline?.totalMinutes ?? 0)
      const minutes = canvasRect ?
        Math.min(Math.max((event.clientX - canvasRect.left) / ppmH, 0), total) :
        0

      return {
        point: { column: Math.max(row, 0), minutes },
        pixel: { x: event.clientX, y: event.clientY }
      }
    }

    const columnCount = Math.max(this._columnCount(), 1)
    const columnWidth = gridRect.width / columnCount
    const column = Math.min(
      Math.max(Math.floor((event.clientX - gridRect.left) / Math.max(columnWidth, 1)), 0),
      columnCount - 1
    )
    const ppm = this._config.hourHeight / 60
    const startMinutes = this._config.dayStartHour * 60
    const minutes = Math.min(
      Math.max(startMinutes + ((event.clientY - gridRect.top) / ppm), startMinutes),
      this._config.dayEndHour * 60
    )

    return {
      point: { column, minutes },
      pixel: { x: event.clientX, y: event.clientY }
    }
  }

  private _occurrenceByKey(key: string): EventOccurrence | undefined {
    return this._occurrences.find(entry => entry.key === key)
  }

  // A week-spanning bar's continuation segments carry the SEGMENT start as
  // their day index; the drag origin must be the event's real start day or
  // the preview lands offset from the actual drop.
  private _monthOriginColumn(occurrence: EventOccurrence, dayIndex: number): number {
    const days = this._month?.days

    if (!days || days.length === 0) {
      return dayIndex
    }

    const index = Math.round(days[0].until(occurrence.start.toPlainDate()).total({ unit: 'days' }))
    return index >= 0 && index < days.length ? index : dayIndex
  }

  private _pressTarget(event: PointerEvent): PressTarget {
    const target = event.target as HTMLElement
    const eventElement = target.closest<HTMLElement>(SELECTOR_EVENT)

    if (!eventElement?.dataset.coreuiKey) {
      return {}
    }

    if (this._store.store.state.view === 'month') {
      const dayIndex = Number(
        eventElement.dataset.coreuiDayIndex ??
        (eventElement.closest(SELECTOR_MONTH_DAY) as HTMLElement | null)?.dataset.coreuiDayIndex ??
        0
      )
      const occurrence = this._occurrenceByKey(eventElement.dataset.coreuiKey)

      if (!occurrence) {
        return { occurrenceKey: eventElement.dataset.coreuiKey }
      }

      return {
        occurrenceKey: occurrence.key,
        origin: { column: this._monthOriginColumn(occurrence, dayIndex), startMinutes: 0, endMinutes: 0 }
      }
    }

    const boxKey = eventElement.dataset.coreuiBoxKey

    if (!boxKey) {
      return {}
    }

    const ref = this._renderer.getBox(boxKey)

    if (!ref) {
      return {}
    }

    const origin: OccurrenceGeometry = {
      column: ref.columnIndex,
      startMinutes: ref.box.startMinutes,
      endMinutes: ref.box.startMinutes + ref.box.height
    }

    if (target.closest(SELECTOR_RESIZE_START)) {
      return { occurrenceKey: ref.box.occurrence.key, handle: 'start', origin }
    }

    if (target.closest(SELECTOR_RESIZE_END)) {
      return { occurrenceKey: ref.box.occurrence.key, handle: 'end', origin }
    }

    return { occurrenceKey: ref.box.occurrence.key, origin }
  }

  private _handleAgendaPointerDown(event: PointerEvent): void {
    const key = ((event.target as HTMLElement).closest(SELECTOR_EVENT) as HTMLElement | null)?.dataset.coreuiKey

    if (this._config.selectable) {
      this._handleClick({ occurrenceKey: key })
    }
  }

  private _handlePointerDown(event: PointerEvent): void {
    if (this._store.store.state.view === 'agenda') {
      return
    }

    if (event.button !== 0 && event.pointerType === 'mouse') {
      return
    }

    if ((event.target as HTMLElement).closest(SELECTOR_DIALOG)) {
      return
    }

    if ((event.target as HTMLElement).closest('[data-part="more-link"]')) {
      return
    }

    const target = this._pressTarget(event)

    if (!target.occurrenceKey && !this._config.selectable) {
      return
    }

    if (target.occurrenceKey && !target.origin) {
      if (this._config.selectable) {
        this._handleClick({ occurrenceKey: target.occurrenceKey })
      }

      return
    }

    if (event.pointerType === 'touch') {
      this._beginTouchPress(event, target)
      return
    }

    event.preventDefault()

    if (this._pointerSession) {
      this._machine.cancel()
    }

    this._pointerSession = { pointerId: event.pointerId }
    this._capturePointer(event.pointerId)
    this._setupWindowListeners()
    this._machine.pointerDown(this._normalizePointer(event), target)
  }

  private _capturePointer(pointerId: number): void {
    try {
      this._element.setPointerCapture(pointerId)
    } catch {}
  }

  private _releasePointer(pointerId: number): void {
    try {
      this._element.releasePointerCapture(pointerId)
    } catch {}
  }

  private _beginTouchPress(event: PointerEvent, target: PressTarget): void {
    this._clearTouchPress()
    const pointer = this._normalizePointer(event)

    window.addEventListener('touchmove', this._onTouchMove, { passive: false })
    window.addEventListener('pointerup', this._onTouchTapUp)

    this._touchPending = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      target,
      timer: setTimeout(() => {
        const pending = this._touchPending

        if (!pending) {
          return
        }

        this._touchPending = null
        window.removeEventListener('pointerup', this._onTouchTapUp)
        this._touchDragging = true
        this._pointerSession = { pointerId: pending.pointerId }
        this._capturePointer(pending.pointerId)
        this._setupWindowListeners()
        this._machine.pointerDown(pointer, pending.target)
      }, TOUCH_DRAG_DELAY_MS)
    }
  }

  private _onTouchTapUp = (event: PointerEvent) => {
    const pending = this._touchPending

    if (!pending || event.pointerId !== pending.pointerId) {
      return
    }

    this._clearTouchPress()

    if (this._config.selectable) {
      this._handleClick({ occurrenceKey: pending.target.occurrenceKey })
    }
  }

  private _clearTouchPress(): void {
    if (this._touchPending) {
      clearTimeout(this._touchPending.timer)
      this._touchPending = null
    }

    if (!this._touchDragging) {
      window.removeEventListener('touchmove', this._onTouchMove)
    }

    window.removeEventListener('pointerup', this._onTouchTapUp)
  }

  private _handlePointerMove(event: PointerEvent): void {
    if (this._pointerSession && event.pointerId === this._pointerSession.pointerId) {
      this._machine.pointerMove(this._normalizePointer(event))
    }
  }

  private _handlePointerUp(event: PointerEvent): void {
    if (this._pointerSession && event.pointerId === this._pointerSession.pointerId) {
      this._pointerSession = null
      this._releasePointer(event.pointerId)
      this._endTouchDrag()
      this._teardownWindowListeners()
      this._machine.pointerUp()
    }
  }

  private _endTouchDrag(): void {
    if (this._touchDragging) {
      this._touchDragging = false
      window.removeEventListener('touchmove', this._onTouchMove)
    }
  }

  private _cancelInteraction(): void {
    if (this._dialog) {
      this._closeDialog()
      return
    }

    this._clearTouchPress()
    this._endTouchDrag()

    if (this._pointerSession) {
      this._releasePointer(this._pointerSession.pointerId)
    }

    this._pointerSession = null
    this._keyboardGrab = null
    this._slotSelection = null
    this._teardownWindowListeners()

    if (this._machine.getState().type !== 'idle') {
      this._machine.cancel()
      this._renderer.announce(this._labels.eventCancelled)
    }
  }

  private _handleEventKeydown(event: KeyboardEvent): void {
    const element = (event.target as HTMLElement).closest<HTMLElement>(SELECTOR_EVENT)
    const key = element?.dataset.coreuiKey

    if (!key) {
      return
    }

    const grabbed = this._keyboardGrab === key && this._machine.getState().type !== 'idle'

    if (!grabbed && (event.key === 'Delete' || event.key === 'Backspace')) {
      event.preventDefault()
      this._deleteOccurrence(key)
      return
    }

    if (!grabbed && event.key === 'F2') {
      event.preventDefault()
      this._handleClick({ occurrenceKey: key })
      return
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()

      if (grabbed) {
        this._keyboardGrab = null
        this._machine.commit()
        return
      }

      const origin = this._keyboardOrigin(element as HTMLElement, key)

      if (!origin) {
        return
      }

      this._keyboardGrab = key
      this._machine.grab(key, origin, event.shiftKey && this._isTimeGrid() ? 'resize-end' : 'move')
      this._renderer.announce(
        interpolate(this._labels.eventGrabbed, { title: this._occurrenceByKey(key)?.event.title ?? '' })
      )
      return
    }

    if (!grabbed) {
      return
    }

    const step = this._store.store.state.view === 'month' ?
{ ArrowUp: { columns: -7 }, ArrowDown: { columns: 7 } } :
{
      ArrowUp: { minutes: -this._config.snap },
      ArrowDown: { minutes: this._config.snap }
    }

    const nudges: Record<string, { minutes?: number; columns?: number }> = {
      ...step,
      ArrowLeft: { columns: -1 },
      ArrowRight: { columns: 1 }
    }

    const nudge = nudges[event.key]

    if (nudge) {
      event.preventDefault()
      this._machine.nudge(nudge)
    }
  }

  private _keyboardOrigin(element: HTMLElement, key: string): OccurrenceGeometry | null {
    if (this._store.store.state.view === 'month') {
      const occurrence = this._occurrenceByKey(key)
      const dayIndex = Number(
        element.dataset.coreuiDayIndex ??
        (element.closest(SELECTOR_MONTH_DAY) as HTMLElement | null)?.dataset.coreuiDayIndex ??
        0
      )

      if (!occurrence) {
        return null
      }

      return { column: this._monthOriginColumn(occurrence, dayIndex), startMinutes: 0, endMinutes: 0 }
    }

    if (this._store.store.state.view === 'agenda') {
      return null
    }

    // timeline bars resolve through boxRefs exactly like the grid path below

    const boxKey = element.dataset.coreuiBoxKey
    const ref = boxKey ? this._renderer.getBox(boxKey) : undefined

    if (!ref) {
      return null
    }

    return {
      column: ref.columnIndex,
      startMinutes: ref.box.startMinutes,
      endMinutes: ref.box.startMinutes + ref.box.height
    }
  }

  private _handleSlotKeydown(event: KeyboardEvent): void {
    const slot = (event.target as HTMLElement).closest<HTMLElement>(SELECTOR_TIME_SLOT)
    const column = slot?.closest<HTMLElement>(SELECTOR_COLUMN)

    if (!slot || !column || !this._isTimeGrid()) {
      return
    }

    const { snap } = this._config
    const startMinutes = this._config.dayStartHour * 60
    const endMinutes = this._config.dayEndHour * 60

    if (this._slotSelection) {
      const extensions: Record<string, { minutes?: number; columns?: number }> = {
        ArrowUp: { minutes: -snap },
        ArrowDown: { minutes: snap },
        ArrowLeft: { columns: -1 },
        ArrowRight: { columns: 1 }
      }

      if (event.key === 'Enter') {
        event.preventDefault()
        this._slotSelection = null
        this._machine.pointerUp()
        return
      }

      const extension = extensions[event.key]

      if (extension) {
        event.preventDefault()
        this._slotSelection = {
          column: Math.min(
            Math.max(this._slotSelection.column + (extension.columns ?? 0), 0),
            this._columnCount() - 1
          ),
          minutes: Math.min(Math.max(this._slotSelection.minutes + (extension.minutes ?? 0), startMinutes), endMinutes)
        }
        this._machine.pointerMove({
          point: { column: this._slotSelection.column, minutes: this._slotSelection.minutes },
          pixel: { x: 0, y: 100 }
        })
      }

      return
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      const anchor = {
        column: this._slotFocus.column,
        minutes: (this._config.dayStartHour + this._slotFocus.slot) * 60
      }
      this._slotSelection = { column: anchor.column, minutes: Math.min(anchor.minutes + snap, endMinutes) }
      this._machine.pointerDown({ point: anchor, pixel: { x: 0, y: 0 } })
      this._machine.pointerMove({
        point: { column: this._slotSelection.column, minutes: this._slotSelection.minutes },
        pixel: { x: 0, y: 100 }
      })
      return
    }

    const navigationKey = NAVIGATION_KEYS[event.key]

    if (navigationKey) {
      event.preventDefault()
      this._slotFocus = resolveNavigation(this._slotFocus, navigationKey, {
        columns: this._columnCount(),
        slots: this._config.dayEndHour - this._config.dayStartHour
      })
      this._applySlotFocus(true)
    }
  }

  private _applySlotFocus(focus = false): void {
    for (const active of this._element.querySelectorAll<HTMLElement>(`${SELECTOR_TIME_SLOT}[tabindex="0"]`)) {
      active.tabIndex = -1
    }

    const minutes = (this._config.dayStartHour + this._slotFocus.slot) * 60
    const slot = this._element.querySelector<HTMLElement>(
      `${SELECTOR_COLUMN}[data-coreui-column="${this._slotFocus.column}"] ${SELECTOR_TIME_SLOT}[data-coreui-minutes="${minutes}"]`
    )

    if (slot) {
      slot.tabIndex = 0

      if (focus) {
        slot.focus()
      }
    }
  }

  private _deleteOccurrence(occurrenceKey: string): void {
    const occurrence = this._occurrenceByKey(occurrenceKey)

    if (!occurrence) {
      return
    }

    this._dialog = null

    this._applyPatch(
      {
        action: 'delete',
        event: occurrence.event,
        occurrenceKey: occurrence.recurring ? occurrence.key : undefined,
        scope: occurrence.recurring ? this._config.recurringEditScope : undefined
      },
      occurrence,
      interpolate(this._labels.eventDeleted, { title: occurrence.event.title ?? '' })
    )

    if (this._store.store.state.selection.includes(occurrenceKey)) {
      this._store.setSelection([])
      EventHandler.trigger(this._element, EVENT_SELECTION_CHANGE, { selection: [] })
    }
  }

  private _handleClick(click: { occurrenceKey?: string }): void {
    if (!this._config.selectable) {
      return
    }

    const selection = click.occurrenceKey ? [click.occurrenceKey] : []
    this._dialog = click.occurrenceKey ?? null
    this._dialogFocusPending = Boolean(click.occurrenceKey)
    this._store.setSelection(selection)
    EventHandler.trigger(this._element, EVENT_SELECTION_CHANGE, { selection })
    this._render()
  }

  private _closeDialog(): void {
    if (!this._dialog) {
      return
    }

    const key = this._dialog
    this._dialog = null
    this._render()
    this._renderer.focusEvent(key)
  }

  private _saveDialog(): void {
    const occurrence = this._dialog ? this._occurrenceByKey(this._dialog) : undefined

    if (!occurrence) {
      return
    }

    const value = (part: string) =>
      this._element.querySelector<HTMLInputElement>(`[data-part="${part}"]`)?.value ?? ''

    const title = value('title-input').trim() || undefined
    const start = value('start-input')
    let end = value('end-input')
    const colorInput = this._element.querySelector<HTMLInputElement>('[data-part="color-input"]')
    const color =
      colorInput && colorInput.value !== colorInput.dataset.coreuiInitial ?
        colorInput.value :
        occurrence.event.color
    const rrule = value('repeat-select') || undefined
    const scopeValue = value('scope-select')

    if (!start || !end) {
      return
    }

    const { timeZone } = this._store.store.state
    const startZoned = toZoned(start, timeZone)
    let endZoned = toZoned(end, timeZone)

    if (Temporal.ZonedDateTime.compare(endZoned, startZoned) <= 0) {
      endZoned = startZoned.add(exactDuration(occurrence.start, occurrence.end))
      end = toISO(endZoned)
    }

    if (!occurrence.allDay && this._blocksCommit(occurrence, startZoned, endZoned, occurrence.event.resourceId)) {
      return
    }

    this._dialog = null

    this._applyPatch(
      {
        action: 'update',
        event: {
 ...occurrence.event, title, color, rrule, start, end
},
        occurrenceKey: occurrence.recurring ? occurrence.key : undefined,
        scope: occurrence.recurring ? (scopeValue as RecurringEditScope) : undefined
      },
      occurrence,
      interpolate(this._labels.eventUpdated, { title: title ?? '' })
    )
  }

  private _blocksBusinessHours(start: Temporal.ZonedDateTime, end: Temporal.ZonedDateTime): boolean {
    const hours = this._config.businessHours

    if (!hours?.enforce) {
      return false
    }

    const startMinutes = (start.hour * 60) + start.minute
    const endMinutes = (end.hour * 60) + end.minute
    const sameDay = start.toPlainDate().equals(end.toPlainDate())
    const dayAllowed = !hours.daysOfWeek || hours.daysOfWeek.includes(start.dayOfWeek)

    const inside =
      dayAllowed &&
      sameDay &&
      startMinutes >= hours.startHour * 60 &&
      endMinutes <= hours.endHour * 60

    if (!inside) {
      this._renderer.announce(this._labels.outsideBusinessHours)
    }

    return !inside
  }

  private _blocksCommit(
    occurrence: EventOccurrence | undefined,
    start: Temporal.ZonedDateTime,
    end: Temporal.ZonedDateTime,
    resourceId: string | undefined
  ): boolean {
    return this._blocksBusinessHours(start, end) || this._blocksOverlap(occurrence, start, end, resourceId)
  }

  private _blocksOverlap(
    occurrence: EventOccurrence | undefined,
    start: Temporal.ZonedDateTime,
    end: Temporal.ZonedDateTime,
    resourceId: string | undefined
  ): boolean {
    if (this._config.eventOverlap) {
      return false
    }

    const { view } = this._store.store.state
    const inResourceView = view === 'resource' || view === 'timeline'

    const collision = this._occurrences.some(other => {
      if (other.allDay || other.key === occurrence?.key) {
        return false
      }

      if (inResourceView && other.event.resourceId !== resourceId) {
        return false
      }

      return overlaps(other.start, other.end, { start, end })
    })

    if (collision) {
      this._renderer.announce(this._labels.overlapBlocked)
    }

    return collision
  }

  private _handleCommit(commit: InteractionCommit): void {
    this._keyboardGrab = null

    if (commit.action === 'select') {
      this._createFromSelection(commit.range)
      return
    }

    const occurrence = this._occurrenceByKey(commit.occurrenceKey)

    if (!occurrence) {
      return
    }

    const scope = occurrence.recurring ? this._config.recurringEditScope : undefined
    const { view } = this._store.store.state

    if (view === 'timeline') {
      this._handleTimelineCommit(commit, occurrence, scope)
      return
    }

    if (commit.action === 'move') {
      const isResourceView = view === 'resource'
      const startWall = occurrence.start
        .toPlainDateTime()
        .add({ days: isResourceView ? 0 : commit.deltaColumns })
        .add({ minutes: commit.deltaMinutes })
      const start = startWall.toZonedDateTime(this._store.store.state.timeZone, { disambiguation: 'compatible' })
      const end = start.add(exactDuration(occurrence.start, occurrence.end))
      const resourceId = isResourceView ?
        this._store.store.state.resources[commit.preview.column]?.id :
        occurrence.event.resourceId

      if (occurrence.allDay) {
        const startDate = start.toPlainDate()
        const spanDays = Math.max(
          occurrence.start.toPlainDate().until(occurrence.end.toPlainDate()).total({ unit: 'days' }),
          1
        )
        this._applyPatch(
          {
            action: 'move',
            event: {
 ...occurrence.event, start: startDate.toString(), end: startDate.add({ days: spanDays }).toString(), resourceId
},
            occurrenceKey: occurrence.recurring ? occurrence.key : undefined,
            scope
          },
          occurrence,
          interpolate(this._labels.eventMoved, {
            title: occurrence.event.title ?? '',
            date: startDate.toString(),
            start: ''
          })
        )
        return
      }

      if (this._blocksCommit(occurrence, start, end, resourceId)) {
        return
      }

      this._applyPatch(
        {
          action: 'move',
          event: {
 ...occurrence.event, start: toISO(start), end: toISO(end), resourceId
},
          occurrenceKey: occurrence.recurring ? occurrence.key : undefined,
          scope
        },
        occurrence,
        interpolate(this._labels.eventMoved, {
          title: occurrence.event.title ?? '',
          date: start.toPlainDate().toString(),
          start: this._formatTime(start)
        })
      )
      return
    }

    const columnDate =
      view === 'month' ?
        this._month?.days[commit.preview.column] :
        this._viewModel?.columns[commit.preview.column]?.date

    if (!columnDate) {
      return
    }

    const start =
      commit.handle === 'start' ?
        this._zonedFromMinutes(columnDate, commit.preview.startMinutes) :
        occurrence.start
    const end =
      commit.handle === 'end' ?
        this._zonedFromMinutes(columnDate, commit.preview.endMinutes) :
        occurrence.end

    if (this._blocksCommit(occurrence, start, end, occurrence.event.resourceId)) {
      return
    }

    this._applyPatch(
      {
        action: 'resize',
        event: { ...occurrence.event, start: toISO(start), end: toISO(end) },
        occurrenceKey: occurrence.recurring ? occurrence.key : undefined,
        scope
      },
      occurrence,
      interpolate(this._labels.eventResized, {
        title: occurrence.event.title ?? '',
        start: this._formatTime(start),
        end: this._formatTime(end)
      })
    )
  }

  private _timelineZoned(offsetMinutes: number): Temporal.ZonedDateTime | null {
    const days = this._timeline?.days

    if (!days) {
      return null
    }

    const dayIndex = Math.min(Math.floor(offsetMinutes / MINUTES_IN_DAY), days.length - 1)
    return this._zonedFromMinutes(days[dayIndex], offsetMinutes - (dayIndex * MINUTES_IN_DAY))
  }

  private _handleTimelineCommit(
    commit: Exclude<InteractionCommit, { action: 'select' }>,
    occurrence: EventOccurrence,
    scope: RecurringEditScope | undefined
  ): void {
    const model = this._timeline

    if (!model) {
      return
    }

    const { preview } = commit
    const targetResource = model.rows[commit.action === 'move' ? preview.column : preview.column]?.resource

    let start: Temporal.ZonedDateTime | null
    let end: Temporal.ZonedDateTime | null

    if (occurrence.allDay) {
      const startDay = Math.round(preview.startMinutes / MINUTES_IN_DAY)
      const spanDays = Math.max(Math.round((preview.endMinutes - preview.startMinutes) / MINUTES_IN_DAY), 1)
      const startDate = model.days[0].add({ days: startDay })
      const event: SchedulerEvent = {
        ...occurrence.event,
        start: startDate.toString(),
        end: startDate.add({ days: spanDays }).toString(),
        resourceId: commit.action === 'move' ? targetResource?.id : occurrence.event.resourceId
      }
      this._applyPatch(
        {
          action: commit.action,
          event,
          occurrenceKey: occurrence.recurring ? occurrence.key : undefined,
          scope
        },
        occurrence,
        interpolate(this._labels.eventUpdated, { title: occurrence.event.title ?? '' })
      )
      return
    }

    if (commit.action === 'move') {
      start = this._timelineZoned(preview.startMinutes)
      end = start ? start.add(exactDuration(occurrence.start, occurrence.end)) : null
    } else {
      start = commit.handle === 'start' ? this._timelineZoned(preview.startMinutes) : occurrence.start
      end = commit.handle === 'end' ? this._timelineZoned(preview.endMinutes) : occurrence.end
    }

    if (!start || !end) {
      return
    }

    const resourceId = commit.action === 'move' ? targetResource?.id : occurrence.event.resourceId

    if (this._blocksCommit(occurrence, start, end, resourceId)) {
      return
    }

    this._applyPatch(
      {
        action: commit.action,
        event: {
 ...occurrence.event, start: toISO(start), end: toISO(end), resourceId
},
        occurrenceKey: occurrence.recurring ? occurrence.key : undefined,
        scope
      },
      occurrence,
      commit.action === 'move' ?
        interpolate(this._labels.eventMoved, {
          title: occurrence.event.title ?? '',
          date: start.toPlainDate().toString(),
          start: this._formatTime(start)
        }) :
        interpolate(this._labels.eventResized, {
          title: occurrence.event.title ?? '',
          start: this._formatTime(start),
          end: this._formatTime(end)
        })
    )
  }

  private _createFromSelection(range: SelectionRange): void {
    const { state } = this._store.store
    const isResourceView = state.view === 'resource'
    uid += 1
    const id = `scheduler-event-${Date.now().toString(36)}-${uid}`

    if (state.view === 'timeline') {
      const start = this._timelineZoned(range.startMinutes)
      const end = this._timelineZoned(range.endMinutes)
      const resource = this._timeline?.rows[range.startColumn]?.resource

      if (!start || !end || !resource) {
        return
      }

      if (this._blocksCommit(undefined, start, end, resource.id)) {
        return
      }

      const event: SchedulerEvent = {
        id,
        title: this._labels.newEvent,
        start: toISO(start),
        end: toISO(end),
        resourceId: resource.id
      }

      this._applyPatch(
        { action: 'create', event },
        undefined,
        interpolate(this._labels.eventCreated, {
          title: event.title ?? '',
          date: start.toPlainDate().toString(),
          start: this._formatTime(start),
          end: this._formatTime(end)
        })
      )
      return
    }

    if (state.view === 'month') {
      const startDate = this._month?.days[range.startColumn]
      const endDate = this._month?.days[range.endColumn]

      if (!startDate || !endDate) {
        return
      }

      const event: SchedulerEvent = {
        id,
        title: this._labels.newEvent,
        start: startDate.toString(),
        end: endDate.add({ days: 1 }).toString(),
        allDay: true
      }

      this._applyPatch(
        { action: 'create', event },
        undefined,
        interpolate(this._labels.eventCreated, {
          title: event.title ?? '',
          date: startDate.toString(),
          start: '',
          end: ''
        })
      )
      return
    }

    const startDate = this._viewModel?.columns[range.startColumn]?.date
    const endDate = isResourceView ? startDate : this._viewModel?.columns[range.endColumn]?.date

    if (!startDate || !endDate) {
      return
    }

    const start = this._zonedFromMinutes(startDate, range.startMinutes)
    const end = this._zonedFromMinutes(
      endDate,
      isResourceView && range.endColumn !== range.startColumn ? MINUTES_IN_DAY : range.endMinutes
    )

    if (this._blocksCommit(undefined, start, end, isResourceView ? state.resources[range.startColumn]?.id : undefined)) {
      return
    }

    const event: SchedulerEvent = {
      id,
      title: this._labels.newEvent,
      start: toISO(start),
      end: toISO(end),
      ...(isResourceView ? { resourceId: state.resources[range.startColumn]?.id } : {})
    }

    this._applyPatch(
      { action: 'create', event },
      undefined,
      interpolate(this._labels.eventCreated, {
        title: event.title ?? '',
        date: start.toPlainDate().toString(),
        start: this._formatTime(start),
        end: this._formatTime(end)
      })
    )
  }

  private _applyPatch(patch: EventPatch, occurrence: EventOccurrence | undefined, announcement: string): void {
    const revert = this._store.applyPatch(patch)
    const occurrencePayload = occurrence ?
      { key: occurrence.key, start: toISO(occurrence.start), end: toISO(occurrence.end) } :
      null

    EventHandler.trigger(this._element, EVENT_EVENT_CHANGE, {
      action: patch.action,
      event: patch.event,
      occurrence: occurrencePayload,
      revert,
      scope: patch.scope ?? null
    })

    this._renderer.announce(announcement)

    const persist = this._config.dataSource?.persistEvents

    if (persist) {
      Promise.resolve(
        persist({
 action: patch.action, event: patch.event, occurrence: occurrencePayload, scope: patch.scope ?? null
})
      )
        .then(result => {
          if (result && result.success === false) {
            revert()
            this._renderer.announce(this._labels.saveFailed)
          }
        })
        .catch(() => {
          revert()
          this._renderer.announce(this._labels.saveFailed)
        })
    }
  }

  private _zonedFromMinutes(date: Temporal.PlainDate, minutes: number): Temporal.ZonedDateTime {
    return date
      .toZonedDateTime({ timeZone: this._store.store.state.timeZone })
      .toPlainDateTime()
      .add({ minutes })
      .toZonedDateTime(this._store.store.state.timeZone, { disambiguation: 'compatible' })
  }

  private _formatTime(value: Temporal.ZonedDateTime): string {
    return new Intl.DateTimeFormat(this._config.locale ?? undefined, {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: this._store.store.state.timeZone
    }).format(new Date(value.epochMilliseconds))
  }
}

export default Scheduler
